/**
 * @file pages/Settings.jsx
 * @description Global application settings page (admin only).
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSettings, updateSetting } from '../api/settings';
import { useToast } from '../context/ToastContext';

export default function Settings() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { data: settings = [], isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn:  listSettings,
  });

  const mutation = useMutation({
    mutationFn: ({ key, value }) => updateSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      addToast('Setting updated', 'success');
    },
    onError: (err) => addToast(err.message, 'danger'),
  });

  const getValue = (key) => settings.find((s) => s.key === key)?.value;

  return (
    <div className="page-content">
      <h1 className="h4 fw-bold mb-4" style={{ color: 'var(--color-primary)' }}>
        <i className="bi bi-gear-fill me-2" aria-hidden="true" />
        Settings
      </h1>

      {error && <div className="alert alert-danger">{error.message}</div>}

      <div className="card">
        <div
          className="card-header fw-semibold"
          style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
        >
          <i className="bi bi-broadcast me-2" aria-hidden="true" />
          Events
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="spinner-border spinner-border-sm" style={{ color: 'var(--color-primary)' }} role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          ) : (
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="emit_events_on_api_requests"
                checked={Boolean(getValue('emit_events_on_api_requests'))}
                onChange={(e) =>
                  mutation.mutate({ key: 'emit_events_on_api_requests', value: e.target.checked })
                }
                disabled={mutation.isPending}
              />
              <label className="form-check-label fw-semibold" htmlFor="emit_events_on_api_requests">
                Emit event on API requests
              </label>
              <div className="form-text">
                When enabled, the backend pushes a live event over Socket.IO every time a
                logged API request completes. The Dashboard's Recent Requests list uses this
                to update automatically without a manual refresh.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
